import Backbone from 'backbone';
import ko from 'knockout';
import _ from 'underscore';
import Promise from 'bluebird';
import config from '@bingads-webui-component-legacy/config/index';
import ViewModel from '@bingads-webui-campaign-legacy/crud-ad/viewmodel/create';
import AdGroupAdsEditor from '@bingads-webui-campaign-legacy/crud-ad/view/adGroupAdsEditor';
import createTemplate from '@bingads-webui-campaign-legacy/crud-ad/template/create.pug';
import appAssociationsEditor from '@bingads-webui-campaign-legacy/adextension-app/view/associationsEditor';
import locationAssociationsEditor from '@bingads-webui-campaign-legacy/adextension-location/view/associationsEditor';
import sitelinkAssociationsEditor from '@bingads-webui-campaign-legacy/adextension-sitelink/view/associationsEditor';
import callAssociationsEditor from '@bingads-webui-campaign-legacy/adextension-call/view/associationsEditor';
import calloutAssociationsEditor from '@bingads-webui-campaign-legacy/adextension-callout/view/associationsEditor';
import reviewAssociationsEditor from '@bingads-webui-campaign-legacy/adextension-review/view/associationsEditor';
import structuredsnippetAssociationsEditor from '@bingads-webui-campaign-legacy/adextension-structuredsnippet/view/associationsEditor';
import priceAssociationsEditor from '@bingads-webui-campaign-legacy/adextension-price/view/associationsEditor';
import promotionAssociationsEditor from '@bingads-webui-campaign-legacy/adextension-promotion/view/associationsEditor';
import filterLinkAssociationsEditor from '@bingads-webui-campaign-legacy/adextension-filter-link/view/associationsEditor';
import flyerAssociationsEditor from '@bingads-webui-campaign-legacy/adextension-flyer/view/associationsEditor';
import actionlinkAssociationsEditor from '@bingads-webui-campaign-legacy/adextension-actionlink/view/associationsEditor';
import { universalItemTemplate as actionUniversalItemTemplate } from '@bingads-webui-campaign-legacy/adextension-actionlink/template';
import * as schemaMapper from '@bingads-webui-campaign-legacy/crud-ad/schemaMapper';
import { getODataErrors, getBulkApiErrors, isCampaignApiError } from '@bingads-webui-component-legacy/api-errors-campaign/index';
import withUiBlock from '@bingads-webui-component-legacy/uiblocker/index';
import toggleDecorator from '@bingads-webui-component-legacy/dialog/toggleDecorator';
import { appendLearnMoreLink } from '@bingads-webui-component-legacy/help/index';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import * as scrollUtil from '@bingads-webui-component-legacy/extension/scrollTo';
import instrumentation from '@bingads-webui-static-legacy/instrumentation/index';
import constants from '@bingads-webui-static-legacy/instrumentation/constants';
import { adGroupTypeEnum } from '@bingads-webui-campaign-legacy/adgroup-create-advanced/util/adGroupTypeEnum';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');

const availableAdTypes = ['Text', 'AppInstall', 'DynamicSearch', 'ExpandedText', 'ResponsiveSearch'];

function splitAds(change, action) {
  return _.chain(change)
    .pluck(action)
    .flatten()
    .groupBy('Type')
    .value();
}

function changes(viewList, adGroups) {
  const change = _.map(adGroups, (adGroup) => {
    return viewList[adGroup.Id].changes();
  });

  const added = splitAds(change, 'addedAds');
  const removed = splitAds(change, 'removedAdIds');

  return {
    added,
    removed,
    empty: _.isEmpty(_.flatten(_.values(added))) && _.isEmpty(_.flatten(_.values(removed))),
  };
}

const editorViewMapper = {
  App: appAssociationsEditor,
  Call: callAssociationsEditor,
  Callout: calloutAssociationsEditor,
  Location: locationAssociationsEditor,
  Review: reviewAssociationsEditor,
  SiteLink: sitelinkAssociationsEditor,
  StructuredSnippet: structuredsnippetAssociationsEditor,
  Price: priceAssociationsEditor,
  Promotion: promotionAssociationsEditor,
  FilterLink: filterLinkAssociationsEditor,
  Flyer: flyerAssociationsEditor,
  Action: actionlinkAssociationsEditor,
};

const permissions = config.get('Permissions') || {};

export default class CreateView extends Backbone.View {
  initialize({
    context = null,
    Campaign: campaign = null,
    campaignType = 'search',
    perfMarker = null,
    usePanel = false,
    isForVNext = false,
    newI18n,
    adExtensionsDynamicFeatures,
    activityLogger,
    serverContext,
  }) {
    this.context = context;
    this.campaign = campaign;
    this.campaignType = campaignType;
    this.perfMarker = perfMarker;
    this.usePanel = usePanel;
    this.isForVNext = isForVNext;
    this.newI18n = newI18n;
    this.activityLogger = activityLogger;
    this.serverContext = serverContext;

    if (this.perfMarker) {
      this.showAdAndExtensionMarker = this.perfMarker.createChild('Show_Ad_And_Extension', {
        autoEnd: true,
      });
      this.showAdAndExtensionMarker.willInit();
    }

    // order is required
    this.availableExtensions = _.chain([
      ['Action', permissions.IsActionLinkExtensionAllowed],
      ['Price', permissions.IsPriceExtensionAllowed && !permissions.IsDomesticSimplifiedChineseEnabled],
      ['Location', permissions.IsLocationExtensionAllowed && !permissions.IsDomesticSimplifiedChineseEnabled],
      ['App', permissions.IsAppExtensionAllowed && !permissions.IsDomesticSimplifiedChineseEnabled],
      ['SiteLink', permissions.IsSiteLinkExtensionsAllowed],
      ['Call', permissions.IsCallExtensionAllowed && !permissions.IsDomesticSimplifiedChineseEnabled],
      ['Callout', permissions.IsCalloutExtensionAllowed && !permissions.IsDomesticSimplifiedChineseEnabled],
      ['Review', permissions.IsReviewExtensionAllowed && !permissions.IsDomesticSimplifiedChineseEnabled],
      ['StructuredSnippet', permissions.IsStructuredSnippetExtensionAllowed && !permissions.IsDomesticSimplifiedChineseEnabled],
      ['Promotion', this.isForVNext && permissions.IsPromotionAdExtensionEnabled],
      ['FilterLink', this.isForVNext && permissions.IsFilterLinkAdExtensionEnabled],
      ['Flyer', this.isForVNext && permissions.IsFlyerAdExtensionEnabled && !permissions.IsDomesticSimplifiedChineseEnabled],
    ]).filter(([, allowed]) => allowed).map(([name]) => name).value();

    const adExtensionsStaticFeatures = {
      Action: {
        universalItemTemplate: actionUniversalItemTemplate,
      },
    };

    this.viewModel = new ViewModel({
      availableExtensions: this.availableExtensions,
      adExtensionsDynamicFeatures,
      adExtensionsStaticFeatures,
    });

    this.viewList = {};
    this.objective = config.get('CampaignObjective');
    this.suggestedExtensions = [];
    this.openedExtEditor = [];

    this.excuteObjective('initialize');
  }

  excuteObjective(method) {
    _
      .chain(this.objectiveActions)
      .result(this.objective)
      .result(method);

    return _.result(this.objectiveActions.All, method);
  }

  get events() {
    return {
      'click .create-extension': 'createExtension',
      'click .edit-extension': 'editExtension',
      'show.bs.collapse': 'toggleExpander',
      'hide.bs.collapse': 'toggleExpander',
    };
  }

  logActivities(message, api = 'ad creation') {
    if (this.activityLogger) {
      this.activityLogger.signal(message, api);
    }
  }

  get objectiveActions() {
    return {
      'WebsiteVisits': {
        initialize: () => {
          this.suggestedExtensions = ['SiteLink', 'Action', 'Callout', 'Promotion'];
          this.otherExtensions = ['Price', 'StructuredSnippet', 'FilterLink', 'Review', 'Call', 'Location', 'Flyer'];
        },
      },
      'LocationVisits': {
        initialize: () => {
          this.suggestedExtensions = ['Location', 'Call', 'Action', 'Promotion'];
          this.otherExtensions = ['Review', 'Callout', 'StructuredSnippet', 'FilterLink', 'Price', 'SiteLink', 'Flyer'];
        },
      },
      'WebsiteConversions': {
        initialize: () => {
          this.suggestedExtensions = ['Action', 'Price', 'Promotion', 'SiteLink'];
          this.otherExtensions = ['Callout', 'StructuredSnippet', 'FilterLink', 'Review', 'Location', 'Call', 'Flyer'];
        },
      },
      'PhoneCalls': {
        initialize: () => {
          this.suggestedExtensions = ['Call', 'Callout', 'Review', 'SiteLink'];
          this.otherExtensions = ['StructuredSnippet', 'FilterLink', 'Price', 'Promotion', 'Action', 'Flyer'];
        },
      },
      'AppInstalls': {
        initialize: () => {
          this.suggestedExtensions = ['App', 'Review'];
          this.otherExtensions = _.difference(this.availableExtensions, this.suggestedExtensions);
        },
      },
      'All': {
        initialize: () => {
          // keep order of suggested and "other" extensions
          this.suggestedExtensions = _.intersection(this.suggestedExtensions, this.availableExtensions);
          this.otherExtensions = _.intersection(this.otherExtensions, this.availableExtensions);
        },
        willShow: () => {
          _.each(this.suggestedExtensions, (ext) => {
            this.openExtensionPanel(ext);
          });
        },
      },
    };
  }

  toggleExpander(evt) {
    const { target } = evt;
    const { id } = target;

    if (id === 'dynamicAdGroupAdEditorSection') {
      this.viewModel.isDynamicSectionExpanded(!this.viewModel.isDynamicSectionExpanded());
    } else if (id === 'standardAdGroupAdEditorSection') {
      this.viewModel.isStandardSectionExpanded(!this.viewModel.isStandardSectionExpanded());
    } else {
      this.viewModel.isAdExtensionExpanded(!this.viewModel.isAdExtensionExpanded());
    }
  }

  willShow() {

    window.scrollTo(0, 0);

    this.campaign = this.context.get('Campaign');
    this.defaultLandingPageUrl = this.context.get('LandingPageUrl') || '';
    this.viewModel.hasChanges(false);

    const dynamicDescriptionEnabled = !_.isEmpty(this.campaign) && this.campaign.DynamicDescriptionEnabled;

    if (_.isUndefined(this.dynamicDescriptionEnabled) && !_.isUndefined(dynamicDescriptionEnabled)) {
      // campaign has dsa settings, initialize the value of dynamicDescriptionEnabled
      this.dynamicDescriptionEnabled = dynamicDescriptionEnabled;
    } else if (dynamicDescriptionEnabled !== this.dynamicDescriptionEnabled) {
      // dynamic description changed, trigger the update to existing ad views
      _.each(this.viewList, (view) => {
        view.trigger('update:dynamicDescriptionEnabled', dynamicDescriptionEnabled);
      });
      this.dynamicDescriptionEnabled = dynamicDescriptionEnabled;
    }

    if (!_.isEmpty(this.campaign)) {
      const adGroups = this.context.get('AdGroups');

      this.excuteObjective('willShow');


      if (!_.isEmpty(adGroups)) {
        const originalIds = _.pluck(this.viewModel.adGroups(), 'Id');
        const newIds = _.pluck(adGroups, 'Id');

        const added = _.difference(newIds, originalIds);
        const removed = _.difference(originalIds, newIds);

        _.each(adGroups, (adgroup) => {
          if (_.contains(added, adgroup.Id)) {
            //add the adgroup which was created in previous step
            this.viewModel.adGroups.push(adgroup);
          } else if (!_.contains(removed, adgroup.Id)) {
            //refresh the adgroup which exist (keywords, name)
            this.viewList[adgroup.Id].viewModel.refresh(adGroups);
          }
        });

        if (!_.isEmpty(added)) {
          this.initializeAdGroupEditorView(added);
        }

        if (!_.isEmpty(removed)) {
          //remove the removed adgroups' backbone and view instance
          _.each(removed, (id) => {
            const removedAdGroup = _.findWhere(this.viewModel.adGroups(), { Id: id });

            this.viewModel.adGroups.remove(removedAdGroup);
            this.viewList[id].remove();
            delete this.viewList[id];
          });
        }
      }
    }
  }

  hasChanges() {
    return this.viewModel.hasChanges();
  }

  isMixedModeCampaignCreation() {
    const isDSAMixedModeCampaignEnabled = _.result(config.get('Permissions'), 'IsDSAMixedModeCampaignEnabled', false);

    return isDSAMixedModeCampaignEnabled && this.campaignType === 'search';
  }

  initializeAdGroupEditorView(adGroups) {
    _.each(adGroups, (id) => {
      this.viewList[id] = new AdGroupAdsEditor({
        newI18n: this.newI18n,
        adGroups: this.viewModel.adGroups(),
        adGroupId: id,
        Campaign: this.campaign,
        el: this.$el.find(`.adgroup-creative[data-id=${id}]`).find('.adgroup-ads-creative'),
        Language: this.campaign.Language,
        Languages: this.campaign.Languages,
        campaignType: this.campaignType,
        defaultLandingPageUrl: this.defaultLandingPageUrl,
        usePanel: this.usePanel,
        activityLogger: this.activityLogger,
        isForVNext: this.isForVNext,
        isMixedModeCampaignCreation: this.isMixedModeCampaignCreation(),
        perfMarker: this.perfMarker,
        serverContext: this.serverContext,
      }).render();

      this.viewList[id].on('copyToOthers', ({ sourceId, ad }) => {
        let adgroupsToPaste = this.viewModel.adGroups();
        const fromAdGroup = _.find(adgroupsToPaste, (adg) => adg.Id === sourceId);

        if (this.isMixedModeCampaignCreation()) {
          const dynamicAdGroups = this.viewModel.dynamicAdGroups();
          const standardAdGroups = this.viewModel.standardAdGroups();
          const { AdGroupType } = fromAdGroup;

          if (AdGroupType === adGroupTypeEnum.SearchStandard) {
            adgroupsToPaste = standardAdGroups;
          } else {
            adgroupsToPaste = dynamicAdGroups;
          }
        }

        _.each(adgroupsToPaste, (adgroup) => {
          const { Id } = adgroup;

          if (Id !== sourceId) {
            this.viewList[Id].copyAdFromOtherAdGroup(ad);
          }
        });
      });

      this.viewList[id].on('copyToSome', ({ ad, targetAdGroupIds }) => {
        _.each(targetAdGroupIds, (adGroupId) => {
          this.viewList[adGroupId].copyAdFromOtherAdGroup(ad);
        });
      });

      this.viewList[id].on('ads:changed', () => {
        this.viewModel.hasChanges(true);
      });
    });
  }

  render() {
    const isDSAMixedModeCampaignEnabled = _.result(config.get('Permissions'), 'IsDSAMixedModeCampaignEnabled', false);

    this.$el.html(createTemplate({
      appendLearnMoreLink,
      i18n,
      objective: this.objective,
      suggestedExtensions: this.suggestedExtensions,
      otherExtensions: this.otherExtensions,
      adExtensionDynamicFeatures: this.viewModel.adExtensionDynamicFeatures,
      adExtensionStaticFeatures: this.viewModel.adExtensionStaticFeatures,
      isDSAMixedModeCampaignEnabled,
    }));
    ko.applyBindings(this.viewModel, this.el);

    this.initializeAdGroupEditorView(_.pluck(this.viewModel.adGroups(), 'Id'));

    return this;
  }

  validate() {
    //check if there is any empty adgroups
    return _.chain(this.viewList)
      .map((view) => {
        view.clearAlertMessages();
        const hasAds = !view.isEmpty();
        const hasUniqAds = !view.viewModel.hasDuplicateAds();

        if (!hasAds) {
          this.logActivities('invalid: has no ads');
        }
        if (!hasUniqAds && this.activityLogger) {
          this.logActivities('invalid: has duplicate ads');
        }

        return hasAds && hasUniqAds;
      })
      .every()
      .value();
  }

  shouldCancel() {
    return this.save();
  }

  shouldNext() {
    return this.save();
  }

  shouldBack() {
    return true;
  }

  errorElements() {
    return this.$('.adgroup-ads__container .error, .adgroup-ads__container .alert-danger').filter(':visible');
  }

  save() {
    if (!this.validate()) {
      scrollUtil.scrollAndCenter(this.errorElements());

      const isDSAMixedModeCampaignEnabled = _.result(config.get('Permissions'), 'IsDSAMixedModeCampaignEnabled', false);

      if (isDSAMixedModeCampaignEnabled) {
        const $dynamicAdGroupSection = this.$('#dynamicAdGroupAdEditorSection');
        const $standardAdGroupSection = this.$('#standardAdGroupAdEditorSection');

        if ($dynamicAdGroupSection) {
          $dynamicAdGroupSection.collapse('show');
        }
        if ($standardAdGroupSection) {
          $standardAdGroupSection.collapse('show');
        }
      }
      return false;
    }

    if (this.perfMarker) {
      this.saveAdAndExtensionMarker = this.perfMarker.createChild('Save_Ad_And_Extension');
      this.saveAdAndExtensionMarker.willInit();
    }

    this.viewModel.errors.clear(isCampaignApiError);

    const results = changes.call(this, this.viewList, this.viewModel.adGroups());

    if (results.empty) {
      return true;
    } else {
      const adsToCreate = {
        Id: this.campaign.Id,
        Name: this.campaign.Name,
      };

      _.each(results.added, (value, key) => {
        adsToCreate[`${key}Ads`] = _.map(value, (ad) => {
          return _.pick(ad, schemaMapper[key](), 'AdGroup');
        });
      });

      return Promise.join(
        _.each(_.flatten(_.values(results.removed)), ({ Id, AdGroup: { Id: adGroupId } }) => {
          jsdata.Ad.destroy(Id, { adapter: 'bulk', parentId: adGroupId });
        }),
      ).then(() => {
        _.each(results.removed, (value, key) => {
          const count = _.size(value);

          if (count > 0) {
            instrumentation.logMessage([constants.LOGCATEGORY.TRACE], constants.LOGLEVEL.INFO, `delete${key}Ad`, count);
          }
        });

        return jsdata.Campaign.update(this.campaign.Id, adsToCreate, { adapter: 'bulk', cacheResponse: false })
          .then((response) => {
            return this.handleSave(response, results.added);
          })
          .catch((response) => {
            _.chain(response.errors).map((error) => error.Error).uniq().each((error) => {
              this.logActivities(error, 'ad creation api error');
            });

            if (response.errors && !response.entity) {
              //Ajax error [400] - RowCountExceeded
              this.viewModel.errors.add(getODataErrors(response.errors));
              return false;
            }

            const requestResult = this.handleSave(response.entity, results.added);

            if (!requestResult) {
              scrollUtil.scrollAndCenter(this.errorElements());
            }
            return requestResult;
          });
      })
        .catch(({ errors }) => {
          this.viewModel.errors.add(getBulkApiErrors(errors));

          scrollUtil.scrollAndCenter(this.errorElements());

          _.chain(errors).map((error) => error.Error).uniq().each((error) => {
            this.logActivities(error, 'ad creation api error');
          });

          return false;
        })
        .finally(withUiBlock());
    }
  }

  handleSave(response, original) {
    let count = 0;

    _.each(availableAdTypes, (type) => {
      _.each(original[type], (ad, index) => {
        ad.Id = Number(response[`${type}Ads`][index].Id);
        ad.isSaved = true;
      });

      count = _.size(response[`${type}Ads`]);

      if (count > 0) {
        instrumentation.logMessage([constants.LOGCATEGORY.TRACE], constants.LOGLEVEL.INFO, `create${type}Ad`, count);
        this.logActivities(count, `createAd: ${type}`);
      }
    });

    _.each(this.viewList, (view) => {
      //refresh main panel
      view.viewModel.slb.refreshSource();
      // empty the array of deletedAdIds
      view.viewModel.deletedAdIds = [];
    });

    if (this.saveAdAndExtensionMarker) {
      this.saveAdAndExtensionMarker.done();
    }
    return true;
  }

  editExtension(target) {
    const type = target.currentTarget.dataset.target;

    this.logActivities(`edit extension button clicked: ${type}`);
    this.openExtensionPanel(type, true);
  }

  createExtension(target) {
    const type = target.currentTarget.dataset.target;

    this.logActivities(`add extension button clicked: ${type}`);
    this.openExtensionPanel(type);
  }

  openExtensionPanel(type, isEdit = false) {
    if ((!_.contains(this.openedExtEditor, type) && _.result(this.viewModel, type).length === 0) || isEdit) {
      this.openedExtEditor.push(type);

      const EditorCtor = toggleDecorator.decorate(
        editorViewMapper[type],
        {
          getToggleEl: () => this.$(`.associations__container[data-type=${type}] .create-extension, .associations__container[data-type=${type}] .campaign-adextension-list`),
          containerCss: 'panel',
        },
      );

      const editor = new EditorCtor({
        campaignId: this.campaign.Id,
        campaignName: this.campaign.Name,
        entityType: 'campaign',
        hasSubTitle: true,
        showErrorsInline: true,
        popupFront: '.navbar-fixed-top',
        perfMarker: this.showAdAndExtensionMarker,
        usePanel: this.usePanel,
        isForVNext: this.isForVNext,
        vnextContext: {
          dynamicFeatures: this.viewModel.adExtensionDynamicFeatures[type],
        },
        activityLogger: this.activityLogger,
      }).render();

      editor.on('save', (ext) => {
        this.viewModel[type](ext);
        editor.remove();
        this.openedExtEditor = _.without(this.openedExtEditor, type);
      });

      editor.on('cancel', () => {
        editor.remove();
        this.openedExtEditor = _.without(this.openedExtEditor, type);
      });

      editor.on('extensions:changed', () => {
        this.viewModel.hasChanges(true);
      });
    }
  }

  remove() {
    _.each(this.viewList, (view) => {
      view.remove();
    });
    super.remove();
  }
}
